import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Divider, Accordion, AccordionSummary, AccordionDetails, Paper, CircularProgress, List, ListItem, ListItemText } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useNavigate } from 'react-router-dom';
import configSiteUrls from '../../../configs/configSiteUrls';
import { PublicacaoOficialList } from '../types/PublicacaoOficial';
import { GridExpandMoreIcon } from '@mui/x-data-grid';

import { PublicacaoOficialAnexo } from '../types/PublicacaoOficialAnexo';
import DownloadAnexoButton from '../../noticia/create/DownloadAnexoButton';
import { useLazyGetPublicacaoOficialAnexosPublicQuery, useLazyGetPublicacaoOficialAnexosQuery } from '../publicaoOficialAnexoApiSlice';
import { VereadorList } from '../../../../../typesSite/vereador/Vereador';

interface PublicacaoOficialCardProps {
  publicacaoOficial: PublicacaoOficialList;
}

export default function PublicacaoOficialPublicCardForm({ publicacaoOficial }: PublicacaoOficialCardProps) {
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [fetchAnexos, { data: dataAnexo, isLoading }] = useLazyGetPublicacaoOficialAnexosPublicQuery();

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded && !dataAnexo) {
      fetchAnexos({ idPublicacaoOficial: publicacaoOficial.id || '' });
    }
  };

  if (!publicacaoOficial) {
    return <div>Erro: dados não carregados!</div>;
  }

  return (
    <Card key={publicacaoOficial.id} id={publicacaoOficial.id} sx={{ width: '100%', mt: 1 }}>
      <CardContent sx={{ p: 1 }}>
        {/* Linha 1 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 0.5, mb: 0.5 }}>
          <Typography variant="caption" color="secondary">
            Exercício: {publicacaoOficial?.exercicio || 'N/a'}
          </Typography>
          <Typography variant="caption" color="secondary">
            | Tipo: {publicacaoOficial?.tipo.nome}
          </Typography>
          <Typography variant="caption" color="secondary">
            | Descrição: {publicacaoOficial?.descricao}
          </Typography>
        </Box>

        {/* Linha 2 (Observacao em destaque, sem borda embaixo) */}
        <Box sx={{ p: 0, display: 'flex' }}>
          <Typography
            variant="caption"
            color="primary"
            sx={{
              fontWeight: 400, // leve, não fica grosso
              fontSize: { xs: '14px', sm: '16px', md: '18px' }, // mais harmonioso
              lineHeight: { xs: '20px', sm: '22px', md: '24px' } // mais respiro
            }}
          >
            {publicacaoOficial.observacao}
          </Typography>
        </Box>
      </CardContent>

      {(publicacaoOficial.temAnexo || publicacaoOficial.vereadores) && (
        <>
          <Divider />
          <Accordion defaultExpanded={false} sx={{ p: 0, m: 0 }}>
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel2-content" id="panel4-header">
              <PushPinOutlinedIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
              <Typography color="primary" variant="body1" gutterBottom>
                Saiba mais
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Accordion defaultExpanded={false} expanded={expanded} onChange={handleAccordionChange}>
                <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel2-content" id="panel4-header">
                  <PushPinOutlinedIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                  <Typography color="primary" variant="body1" gutterBottom>
                    Anexos
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                      <Typography sx={{ ml: 1 }} variant="body2" color="textSecondary">
                        Carregando anexos...
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && dataAnexo?.length === 0 && <Typography>Nenhum anexo disponível</Typography>}

                  {!isLoading && (
                    <List dense>
                      {dataAnexo?.map((file: PublicacaoOficialAnexo) => (
                        <ListItem key={file.path} disablePadding secondaryAction={<DownloadAnexoButton tipo="PUBLICACAO_OFICIAL" ida={file.path} fileName={file.nomeOriginal} />}>
                          <ListItemText primary={file.nomeOriginal} primaryTypographyProps={{ variant: 'body2', color: 'secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>

              <Divider />
              <Accordion defaultExpanded={false} sx={{ p: 0, m: 0 }}>
                <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel2-content" id="panel4-header">
                  <PushPinOutlinedIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                  <Typography color="primary" variant="body1" sx={{ p: 0, m: 0 }}>
                    Autores / Coautores
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 1, m: 0 }}>
                  {!isLoading && (
                    <List dense>
                      {publicacaoOficial.vereadores.map((vereador: VereadorList) => (
                        <ListItem key={vereador.id} disablePadding>
                          <ListItemText primary={vereador.nome} primaryTypographyProps={{ color: 'secondary', variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Card>
  );
}
